import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';
import { qk } from './keys';
import type { Subject } from '../types/api';
import type {
  LearningPlan,
  PlanAdjustmentCandidate,
  PlanAdjustmentInput,
  PlanAdjustmentPreview,
  PlanChange,
  PlanClosure,
  PlanOffer,
  PlanOfferInput,
  PlanTeacherCandidate,
  TeachingRequest,
  PlanAudit,
} from '../types/learning-plans';

export function useLearningPlans() {
  return useQuery({
    queryKey: qk.learningPlans,
    refetchInterval: 30000,
    queryFn: async () => {
      const [requests, offers, changes, closures, subjects, grades, audit] = await Promise.all([
        api.get<LearningPlan[]>('/admin/learning-plans'),
        api.get<PlanOffer[]>('/admin/learning-plans/offers'),
        api.get<PlanChange[]>('/admin/learning-plan-changes'),
        api.get<PlanClosure[]>('/learning-plan-closures'),
        api.get<{ id: string; nameEn: string; nameAr?: string; slug: string }[]>('/catalog/subjects', { activeOnly: false }),
        api.get<{ id: string; nameEn: string; nameAr?: string; levelId: string }[]>('/catalog/grades', {
          country: 'SA', activeOnly: false,
        }),
        api.get<PlanAudit[]>('/admin/learning-plans/audit'),
      ]);
      const otherCountries = [...new Set(offers.map((offer) => offer.countryCode))].filter((country) => country !== 'SA');
      const otherGrades = await Promise.all(otherCountries.map((country) =>
        api.get<{ id: string; nameEn: string; nameAr?: string; levelId: string }[]>('/catalog/grades', { country, activeOnly: false })));
      return { requests, offers, changes, closures, subjects, grades: [...grades, ...otherGrades.flat()], audit };
    },
  });
}
// The home page's worklist needs only requests and family changes, not the
// offers, catalog and audit the plans page loads. Keyed under learningPlans so
// every plan mutation refreshes it too.
export function useLearningPlanWork(): UseQueryResult<{ requests: LearningPlan[]; changes: PlanChange[] }, Error> {
  return useQuery({
    queryKey: [...qk.learningPlans, 'work'],
    refetchInterval: 30000,
    queryFn: async () => {
      const [requests, changes] = await Promise.all([
        api.get<LearningPlan[]>('/admin/learning-plans'),
        api.get<PlanChange[]>('/admin/learning-plan-changes'),
      ]);
      return { requests, changes };
    },
  });
}

export function useTeachingResponses(id: string) {
  return useQuery({
    queryKey: [...qk.learningPlans, 'teachers', id],
    queryFn: () => api.get<TeachingRequest[]>(`/admin/learning-plans/${id}/teachers`),
    enabled: Boolean(id),
    refetchInterval: 10000,
  });
}
export function useTeacherCandidates(id: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.teacherCandidates(id),
    queryFn: () => api.get<PlanTeacherCandidate[]>(`/admin/learning-plans/${id}/teacher-candidates`),
    enabled,
  });
}
export function useLearningPlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body, method = 'POST' }: { path: string; body?: unknown; method?: 'POST' | 'DELETE' }) =>
      method === 'DELETE' ? api.delete<unknown>(path) : api.post<unknown>(path, body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.learningPlans }),
        qc.invalidateQueries({ queryKey: qk.adminSessionsAll }),
      ]);
    },
  });
}

export function useReplacementCandidates(id: string, target: { targetKind: 'lesson' | 'change'; targetId: string; startTime?: string }, enabled: boolean): UseQueryResult<{ tutorId: string; tutorName: string }[], Error> {
  return useQuery({
    queryKey: qk.replacementCandidates(id, target),
    queryFn: () => api.post<{ tutorId: string; tutorName: string }[]>(`/admin/learning-plans/${id}/replacement-candidates`, target),
    enabled,
  });
}

// Preview and candidates are reads that happen to take a body; keyed by the
// whole input so editing a field shows that input's result, never a stale one.
export function useAdjustmentPreview(id: string, input: PlanAdjustmentInput | null): UseQueryResult<PlanAdjustmentPreview, Error> {
  return useQuery({
    queryKey: qk.adjustmentPreview(id, input ?? {}),
    queryFn: () => api.post<PlanAdjustmentPreview>(`/admin/learning-plans/${id}/adjustments/preview`, input),
    enabled: input !== null,
    retry: false,
  });
}

export function useAdjustmentCandidates(id: string, input: PlanAdjustmentInput | null): UseQueryResult<PlanAdjustmentCandidate[], Error> {
  return useQuery({
    queryKey: qk.adjustmentCandidates(id, input ?? {}),
    queryFn: () => api.post<PlanAdjustmentCandidate[]>(`/admin/learning-plans/${id}/adjustments/candidates`, input),
    enabled: input !== null,
    retry: false,
  });
}

export function useApplyAdjustment(): UseMutationResult<LearningPlan, Error, { id: string; input: PlanAdjustmentInput }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PlanAdjustmentInput }) =>
      api.post<LearningPlan>(`/admin/learning-plans/${id}/adjustments`, { ...input, familyAgreed: true }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.learningPlans }),
        qc.invalidateQueries({ queryKey: qk.adminSessionsAll }),
      ]);
    },
  });
}

interface OfferCatalog {
  subjects: Subject[];
  countries: { code: string; nameEn: string; nameAr?: string }[];
  grades: { id: string; levelId: string; nameEn: string; nameAr?: string }[];
  levels: { id: string; slug: string; nameEn: string; nameAr?: string }[];
}

export function useOfferCatalog(country: string): UseQueryResult<OfferCatalog, Error> {
  return useQuery({
    queryKey: qk.offerCatalog(country),
    queryFn: async () => {
      const [subjects, countries, grades, levels] = await Promise.all([
        api.get<Subject[]>('/catalog/subjects', { country }),
        api.get<{ code: string; nameEn: string; nameAr?: string }[]>('/catalog/countries', { registrationOpen: true }),
        api.get<{ id: string; levelId: string; nameEn: string; nameAr?: string }[]>('/catalog/grades', { country }),
        api.get<{ id: string; slug: string; nameEn: string; nameAr?: string }[]>('/catalog/levels', { country }),
      ]);
      return { subjects, countries, grades, levels };
    },
  });
}

interface SavedOffer { offer: PlanOffer; publicationError?: unknown }

export function useSavePlanOffer(): UseMutationResult<SavedOffer, Error, { offer: PlanOfferInput; publish: boolean }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ offer, publish }: { offer: PlanOfferInput; publish: boolean }): Promise<{ offer: PlanOffer; publicationError?: unknown }> => {
      const saved = await api.post<PlanOffer>('/admin/learning-plans/offers', offer);
      if (!publish) return { offer: saved };
      try {
        return { offer: await api.post<PlanOffer>(`/admin/learning-plans/offers/${saved.id}/publish`, { published: true }) };
      } catch (publicationError) {
        // The draft already exists: return its identity so retrying only publishes it.
        return { offer: saved, publicationError };
      }
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: qk.learningPlans }); },
  });
}
